'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface CompletedSession {
  id: string;
  label: string;
  consultantName: string;
  consultantInitials: string;
  consultantTitle: string;
  service: string;
  date: string;
  revieweeId: string;
  bookingId: string;
}

interface CategoryRating {
  key: string;
  label: string;
  icon: string;
  value: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const REVIEW_TAGS = [
  'Very Knowledgeable',
  'Great Communicator',
  'Punctual',
  'Patient & Supportive',
  'Practical Advice',
  'Well Prepared',
  'Clear Explanations',
  'Highly Recommended',
  'Professional',
  'Exceeded Expectations',
  'Good Listener',
  'Actionable Insights',
];

const INITIAL_CATEGORY_RATINGS: CategoryRating[] = [
  { key: 'knowledge', label: 'Knowledge & Expertise', icon: 'AcademicCapIcon', value: 0 },
  { key: 'communication', label: 'Communication', icon: 'ChatBubbleLeftRightIcon', value: 0 },
  { key: 'punctuality', label: 'Punctuality', icon: 'ClockIcon', value: 0 },
  { key: 'value', label: 'Value for Money', icon: 'CurrencyDollarIcon', value: 0 },
];

// ─── Star Rating ──────────────────────────────────────────────────────────────
function StarRating({
  value,
  onChange,
  readonly = false,
  size = 'md',
}: {
  value: number;
  onChange?: (v: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-9 h-9' : 'w-6 h-6';

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hovered || value) >= star;
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && onChange?.(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            className={`${sizeClass} transition-all duration-100 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          >
            <svg viewBox="0 0 20 20" fill={filled ? '#f59e0b' : 'none'} stroke={filled ? '#f59e0b' : '#d1d5db'} strokeWidth="1.5">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

// ─── Success Modal ────────────────────────────────────────────────────────────
function SuccessModal({ onClose, onViewReviews }: { onClose: () => void; onViewReviews: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-3xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in-95 duration-300">
        {/* Animated checkmark */}
        <div className="w-20 h-20 rounded-full gradient-teal flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Icon name="CheckIcon" size={40} className="text-white" />
        </div>

        <h2 className="text-2xl font-700 text-foreground mb-2">Review Submitted!</h2>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          Thank you for sharing your feedback. Your review helps the community find the best consultants.
        </p>

        {/* Decorative stars */}
        <div className="flex justify-center gap-1 mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <svg key={s} className="w-6 h-6" viewBox="0 0 20 20" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={onViewReviews}
            className="w-full bg-primary text-primary-foreground font-600 py-3 rounded-xl hover:bg-primary/90 transition-colors duration-150"
          >
            View My Reviews
          </button>
          <button
            onClick={onClose}
            className="w-full bg-muted text-foreground font-500 py-3 rounded-xl hover:bg-muted/80 transition-colors duration-150"
          >
            Submit Another Review
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({
  overallRating,
  selectedSession,
  onConfirm,
  onCancel,
  submitting,
}: {
  overallRating: number;
  selectedSession: CompletedSession | null;
  onConfirm: () => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-3xl shadow-2xl max-w-sm w-full p-7 animate-in fade-in zoom-in-95 duration-200">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
          <Icon name="PaperAirplaneIcon" size={24} className="text-amber-600" />
        </div>
        <h3 className="text-lg font-700 text-foreground mb-1">Submit this review?</h3>
        {selectedSession && (
          <p className="text-sm text-muted-foreground mb-4">
            You&apos;re reviewing <span className="font-600 text-foreground">{selectedSession.consultantName}</span> for{' '}
            <span className="font-600 text-foreground">{selectedSession.service}</span>.
          </p>
        )}
        <div className="flex items-center gap-2 mb-6 p-3 bg-muted rounded-xl">
          <StarRating value={overallRating} readonly size="sm" />
          <span className="text-sm font-600 text-foreground">{overallRating}/5 overall</span>
        </div>
        <p className="text-xs text-muted-foreground mb-6">
          Reviews cannot be edited after submission. Make sure your feedback is accurate and respectful.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 bg-muted text-foreground font-500 py-2.5 rounded-xl hover:bg-muted/80 transition-colors duration-150 text-sm"
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 bg-primary text-primary-foreground font-600 py-2.5 rounded-xl hover:bg-primary/90 transition-colors duration-150 text-sm flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting…
              </>
            ) : (
              'Confirm & Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Form ────────────────────────────────────────────────────────────────
function SubmitReviewContent() {
  const supabase = createClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const prefilledBookingId = searchParams.get('booking');

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [completedSessions, setCompletedSessions] = useState<CompletedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  // Form state
  const [overallRating, setOverallRating] = useState(0);
  const [categoryRatings, setCategoryRatings] = useState<CategoryRating[]>(INITIAL_CATEGORY_RATINGS);
  const [feedback, setFeedback] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);

  // UI state
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedSession = completedSessions.find((s) => s.id === selectedSessionId) ?? null;

  // Auth
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  // Fetch completed sessions
  useEffect(() => {
    async function fetchSessions() {
      if (!currentUserId) { setLoadingSessions(false); return; }
      setLoadingSessions(true);
      try {
        const { data: bookings } = await supabase
          .from('consultation_bookings')
          .select('id, service_title, provider_name, provider_user_id, booking_date, consultant_id')
          .eq('client_user_id', currentUserId)
          .eq('status', 'completed');

        const sessions: CompletedSession[] = (bookings ?? []).map((b) => {
          const dateStr = b.booking_date
            ? new Date(b.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : '';
          const name = b.provider_name ?? 'Consultant';
          const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
          return {
            id: b.id,
            label: `${name} — ${b.service_title ?? 'Session'} (${dateStr})`,
            consultantName: name,
            consultantInitials: initials,
            consultantTitle: 'Consultant',
            service: b.service_title ?? 'Session',
            date: dateStr,
            revieweeId: b.provider_user_id ?? '',
            bookingId: b.id,
          };
        });

        setCompletedSessions(sessions);

        // Pre-select if booking param provided
        if (prefilledBookingId) {
          const match = sessions.find((s) => s.bookingId === prefilledBookingId);
          if (match) setSelectedSessionId(match.id);
        }
      } catch {
        // silently fail
      } finally {
        setLoadingSessions(false);
      }
    }
    fetchSessions();
  }, [currentUserId, prefilledBookingId]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : prev.length < 6 ? [...prev, tag] : prev
    );
  };

  const setCategoryRating = (key: string, value: number) => {
    setCategoryRatings((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  };

  const handleSubmitClick = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!currentUserId) { setError('You must be signed in to submit a review.'); return; }
    if (!selectedSessionId) { setError('Please select a completed session.'); return; }
    if (overallRating === 0) { setError('Please provide an overall star rating.'); return; }
    if (feedback.trim().length < 10) { setError('Please write at least 10 characters of feedback.'); return; }
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    if (!currentUserId || !selectedSession) return;
    setSubmitting(true);
    setError('');

    const avgCategoryRating =
      categoryRatings.filter((r) => r.value > 0).length > 0
        ? Math.round(categoryRatings.filter((r) => r.value > 0).reduce((sum, r) => sum + r.value, 0) / categoryRatings.filter((r) => r.value > 0).length)
        : overallRating;

    const { error: insertError } = await supabase.from('session_reviews').insert({
      booking_id: selectedSession.bookingId,
      reviewer_id: currentUserId,
      reviewee_id: selectedSession.revieweeId || currentUserId,
      reviewer_role: 'client',
      rating: overallRating,
      feedback: feedback.trim(),
      is_visible_on_profile: isVisible,
    });

    setSubmitting(false);
    setShowConfirm(false);

    if (insertError) {
      setError(insertError.message);
    } else {
      setShowSuccess(true);
    }
  };

  const handleReset = () => {
    setShowSuccess(false);
    setSelectedSessionId('');
    setOverallRating(0);
    setCategoryRatings(INITIAL_CATEGORY_RATINGS);
    setFeedback('');
    setSelectedTags([]);
    setWouldRecommend(null);
    setIsVisible(true);
    setError('');
  };

  const ratingLabel = (v: number) => {
    if (v === 0) return '';
    const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
    return labels[v];
  };

  return (
    <AppLayout activeRoute="/submit-review">
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-8">

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => router.back()}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-5"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Back
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl gradient-teal flex items-center justify-center">
                <Icon name="StarIcon" size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-700 text-foreground">Leave a Review</h1>
                <p className="text-sm text-muted-foreground">Share your experience to help others</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmitClick} className="space-y-6">

            {/* Session Selector */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-4 flex items-center gap-2">
                <Icon name="CalendarDaysIcon" size={18} className="text-primary" />
                Select Session
              </h2>
              {loadingSessions ? (
                <div className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-muted-foreground animate-pulse">
                  Loading your completed sessions…
                </div>
              ) : completedSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                    <Icon name="CalendarIcon" size={24} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-500 text-foreground mb-1">No completed sessions</p>
                  <p className="text-xs text-muted-foreground">Sessions become reviewable after they are marked as completed.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedSessions.map((session) => (
                    <label
                      key={session.id}
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 ${
                        selectedSessionId === session.id
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="session"
                        value={session.id}
                        checked={selectedSessionId === session.id}
                        onChange={() => setSelectedSessionId(session.id)}
                        className="sr-only"
                      />
                      <div className="w-10 h-10 rounded-full gradient-teal flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {session.consultantInitials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-600 text-foreground truncate">{session.consultantName}</p>
                        <p className="text-xs text-muted-foreground truncate">{session.service} · {session.date}</p>
                      </div>
                      {selectedSessionId === session.id && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <Icon name="CheckIcon" size={12} className="text-white" />
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Overall Rating */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-5 flex items-center gap-2">
                <Icon name="StarIcon" size={18} className="text-primary" />
                Overall Rating
              </h2>
              <div className="flex flex-col items-center gap-3">
                <StarRating value={overallRating} onChange={setOverallRating} size="lg" />
                {overallRating > 0 && (
                  <span className="text-sm font-600 text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                    {ratingLabel(overallRating)}
                  </span>
                )}
                {overallRating === 0 && (
                  <p className="text-xs text-muted-foreground">Tap a star to rate</p>
                )}
              </div>
            </div>

            {/* Category Ratings */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-5 flex items-center gap-2">
                <Icon name="ChartBarIcon" size={18} className="text-primary" />
                Detailed Ratings
                <span className="text-xs font-400 text-muted-foreground ml-1">(optional)</span>
              </h2>
              <div className="space-y-4">
                {categoryRatings.map((cat) => (
                  <div key={cat.key} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StarRating value={cat.value} onChange={(v) => setCategoryRating(cat.key, v)} size="sm" />
                      {cat.value > 0 && (
                        <span className="text-xs text-muted-foreground w-12 text-right">{ratingLabel(cat.value)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-2 flex items-center gap-2">
                <Icon name="TagIcon" size={18} className="text-primary" />
                Tags
                <span className="text-xs font-400 text-muted-foreground ml-1">(pick up to 6)</span>
              </h2>
              <p className="text-xs text-muted-foreground mb-4">Select tags that best describe your experience</p>
              <div className="flex flex-wrap gap-2">
                {REVIEW_TAGS.map((tag) => {
                  const active = selectedTags.includes(tag);
                  const disabled = !active && selectedTags.length >= 6;
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      disabled={disabled}
                      className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : disabled
                          ? 'bg-muted text-muted-foreground border-border opacity-40 cursor-not-allowed'
                          : 'bg-muted text-foreground border-border hover:border-primary/50 hover:bg-primary/5'
                      }`}
                    >
                      {active && <span className="mr-1">✓</span>}
                      {tag}
                    </button>
                  );
                })}
              </div>
              {selectedTags.length > 0 && (
                <p className="text-xs text-primary mt-3">{selectedTags.length}/6 tags selected</p>
              )}
            </div>

            {/* Written Feedback */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-2 flex items-center gap-2">
                <Icon name="PencilSquareIcon" size={18} className="text-primary" />
                Written Feedback
              </h2>
              <p className="text-xs text-muted-foreground mb-4">Share details about your session experience (min. 10 characters)</p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Describe what went well, what could be improved, and any specific highlights from your session…"
                rows={5}
                maxLength={1000}
                className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none leading-relaxed"
              />
              <div className="flex items-center justify-between mt-2">
                <span className={`text-xs ${feedback.length < 10 && feedback.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {feedback.length < 10 && feedback.length > 0 ? `${10 - feedback.length} more characters needed` : ''}
                </span>
                <span className="text-xs text-muted-foreground">{feedback.length}/1000</span>
              </div>
            </div>

            {/* Would Recommend */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
              <h2 className="text-base font-600 text-foreground mb-4 flex items-center gap-2">
                <Icon name="HandThumbUpIcon" size={18} className="text-primary" />
                Would you recommend this consultant?
              </h2>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setWouldRecommend(true)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-500 text-sm transition-all duration-150 ${
                    wouldRecommend === true
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700' :'border-border hover:border-emerald-300 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="HandThumbUpIcon" size={16} />
                  Yes, definitely
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(false)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-500 text-sm transition-all duration-150 ${
                    wouldRecommend === false
                      ? 'border-red-400 bg-red-50 text-red-600' :'border-border hover:border-red-300 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="HandThumbDownIcon" size={16} />
                  Not this time
                </button>
              </div>
            </div>

            {/* Visibility Toggle */}
            <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
              <label className="flex items-center justify-between cursor-pointer gap-4">
                <div className="flex items-center gap-3">
                  <Icon name={isVisible ? 'EyeIcon' : 'EyeSlashIcon'} size={18} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm font-600 text-foreground">Show on consultant&apos;s profile</p>
                    <p className="text-xs text-muted-foreground">Your review will be publicly visible on their profile page</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsVisible(!isVisible)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${isVisible ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isVisible ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </label>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                <Icon name="ExclamationCircleIcon" size={18} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground font-600 py-4 rounded-2xl hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 text-base flex items-center justify-center gap-2 shadow-lg"
            >
              <Icon name="PaperAirplaneIcon" size={20} />
              Submit Review
            </button>

            <p className="text-center text-xs text-muted-foreground pb-4">
              Reviews are final and cannot be edited after submission. Please ensure your feedback is honest and respectful.
            </p>
          </form>
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirm && (
        <ConfirmModal
          overallRating={overallRating}
          selectedSession={selectedSession}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowConfirm(false)}
          submitting={submitting}
        />
      )}

      {/* Success Modal */}
      {showSuccess && (
        <SuccessModal
          onClose={handleReset}
          onViewReviews={() => router.push('/session-reviews')}
        />
      )}
    </AppLayout>
  );
}

// ─── Page Export ──────────────────────────────────────────────────────────────
export default function SubmitReviewPage() {
  return (
    <Suspense fallback={
      <AppLayout activeRoute="/submit-review">
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AppLayout>
    }>
      <SubmitReviewContent />
    </Suspense>
  );
}
